import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { AsyncPipe, NgClass, NgStyle, NgTemplateOutlet } from '@angular/common';
import { AfterViewInit, Component, inject, Input, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { FormControl } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
	AlertComponent,
	ButtonComponent,
	ButtonIconComponent,
	INavHelp,
	LinkComponent,
	NavHelpComponent,
	ProgressComponent,
	PwcsLayoutComponent,
	ScanFaceComponent,
	SpinnerComponent,
	TileComponent,
	WatermarkComponent,
} from '@tecalisdev/infinity';
import { AnimationOptions, LottieComponent } from 'ngx-lottie';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { ProgressService } from 'src/app/core/helpers/progress.service';
import { LocalStorageKeys, operations } from 'src/app/core/models/auth.models';
import { ILoading } from 'src/app/core/models/loading.models';
import { NavHelpAction } from 'src/app/core/models/navHelp.models';
import { AuthService } from 'src/app/core/services/auth.service';
import { CookieService } from 'src/app/core/services/cookie.service';
import { LoadingService } from 'src/app/core/services/loading.service';
import { ScreenshotService } from 'src/app/core/services/screenshot.service';
import { ModalChangeDeviceComponent } from 'src/app/shared/components/modal-change-device/modal-change-device.component';
import { ModalHelpNoCameraPermissionsComponent } from 'src/app/shared/components/modal-help-no-camera-permissions/modal-help-no-camera-permissions.component';
import { ModalScanQrComponent } from 'src/app/shared/components/modal-scan-qr/modal-scan-qr.component';
import { PwcsInlineSvgComponent } from 'src/app/shared/components/pwcs-inline-svg/pwcs-inline-svg.component';
import { VideoComponent } from 'src/app/shared/components/video/video.component';
import { ERROR_CODE } from 'src/app/shared/models/error.models';
import { IMaster } from 'src/app/shared/models/master.models';
import { AudioService } from 'src/app/shared/services/audio.service';
import { CameraService } from 'src/app/shared/services/camera.service';
import { CameraPermissionsTimerService } from 'src/app/shared/services/camera-permissions-timer.service';
import { waitForVideoPreviewReady } from 'src/app/shared/utils/wait-for-video-preview-ready.util';
import {
	onCameraPreviewSwitchCompleted,
	onCameraPreviewSwitchStarted,
} from 'src/app/shared/utils/camera-preview-switch.util';
import { CloudLogsService, LogType } from 'src/app/shared/services/cloud-logs.service';
import { DesignService } from 'src/app/shared/services/config/design.service';
import { LottieThemeService } from 'src/app/shared/services/config/lottie/lottie-theme.service';
import { ErrorService } from 'src/app/shared/services/error.service';
import { FullscreenService } from 'src/app/shared/services/fullscreen.service';
import { IdentitySDKService, OperationType } from 'src/app/shared/services/identity-sdk.service';
import { MasterService } from 'src/app/shared/services/master.service';
import { LanguagePickerUiService } from 'src/app/shared/services/language-picker-ui.service';
import { NavHelpService } from 'src/app/shared/services/nav-help.service';
import { StreamingManagementService } from 'src/app/shared/services/streaming-management.service';
import { TextToSpeechService } from 'src/app/shared/services/text-to-speech.service';
import { UserAgentService } from 'src/app/shared/services/user-agent.service';
import { drawVideoCoverFrameToCanvas } from 'src/app/shared/services/video-cover-frame.util';
import { environment } from 'src/environments/environment';
import {
	IPolErrorResponse,
	IPolManualConf,
	IPolPayload,
	IPolResponse,
	POL_BACK_TO_OCR_TIME,
	POL_WARNING_TIME,
	PolPreviewDevice,
} from '../../models/life-check';
import {
	PolManualLog,
	IFinishOperation,
	IOperation,
	IPageWarning,
	IPolOperation,
	TEST_STATUS,
	OPERATION_STATUS,
	OPERATION_TYPE,
	PAGE_WARNING,
	quality,
	TestStatus,
} from '../../models/user-identification';
import { LifeCheckService } from '../../services/pol/life-check.service';
import { PolTimerManagerService } from '../../services/pol/timer-manager.service';
import { ProcessService } from '../../services/process.service';
import { KoDebugImagesService } from '../../services/debug/ko-debug-images.service';
import { buildManualPolImagesPayload } from '../../utils/build-manual-pol-images.util';
import {
	getPolAnalysisExtraZoomFactor,
	getPolLivenessJpegCrop,
	getPolStaticPayloadUseFullCanvas,
	getPolStaticPayloadZoomFactor,
	manualLifeCheckConf,
} from '../life-check/life-check-conf';
import { HandsetLandscapeTileComponent } from 'src/app/shared/components/handset-landscape-tile/handset-landscape-tile.component';
import { OperationService } from 'src/app/shared/services/step.service';

/** Intervalo del ring buffer de frames silenciosos (NIF-822). */
const SILENT_FRAME_INTERVAL_MS = 175;
/** Máximo de frames en el ring buffer (NIF-822). */
const SILENT_FRAME_BUFFER_SIZE = 2;

@Component({
	selector: 'app-manual-life-check',
	imports: [
		TranslatePipe,
		AsyncPipe,
		NgClass,
		NgStyle,
		NgTemplateOutlet,
		LottieComponent,
		AlertComponent,
		ButtonComponent,
		ButtonIconComponent,
		LinkComponent,
		NavHelpComponent,
		ProgressComponent,
		PwcsLayoutComponent,
		ScanFaceComponent,
		SpinnerComponent,
		TileComponent,
		WatermarkComponent,
		VideoComponent,
		HandsetLandscapeTileComponent,
		ModalScanQrComponent,
		ModalHelpNoCameraPermissionsComponent,
		ModalChangeDeviceComponent,
		PwcsInlineSvgComponent,
	],
	templateUrl: './manual-life-check.component.html',
	styleUrls: ['./manual-life-check.component.scss'],
	encapsulation: ViewEncapsulation.None,
})
export class ManualLifeCheckComponent implements OnInit, AfterViewInit, OnDestroy {
	operationService = inject(OperationService);

	@Input() operations: IOperation[];
	@Input()
	get operation(): IPolOperation {
		return this._operation;
	}
	set operation(value: IPolOperation) {
		this._operation = value;
		value.config.iframe
			? this.loadingService.showIframe(this.trans.instant('GENERAL.LOADING.CAMERA_INIT'))
			: this.loadingService.show(this.trans.instant('GENERAL.LOADING.CAMERA_INIT'));
		this.activatePreviewLoading('GENERAL.LOADING.CAMERA_INIT');
		this.manualLifeCheckConf = this.setPolSetting(value.config);
		this.manualLifeCheckConf.general.startAt = new Date().toISOString();
		if (this.operation?.config?.iframe) {
			localStorage.setItem(LocalStorageKeys.access_token, operations.old.token);
		} else {
			this.cookieService.set(LocalStorageKeys.access_token, operations.old.token, undefined, '/');
		}

		// Actualizamos el subtitle
		this.updateSubtitle();
	}
	private _operation: IPolOperation;

	// Textos y alertas
	public subtitle: string = '';
	private sounded: Array<string> = [];

	// Controles
	public isHuman: boolean = true;
	public formStats: FormControl = new FormControl(false);
	public controls: any;

	// Camara
	public video!: HTMLVideoElement;
	public canvas!: HTMLCanvasElement;
	public ctx!: CanvasRenderingContext2D;
	public imageData16x9: string;
	public stream: MediaStream;
	public allCameras: Array<MediaDeviceInfo> = [];

	// Configuración de la prueba
	public manualLifeCheckConf: IPolManualConf;
	private subscriptionTimers: Subscription = new Subscription();

	// General
	private initOperation: boolean = false;
	public env = environment;
	public loading: ILoading = {
		show: false,
		text: '',
	};
	public isVisibleScan: boolean = false;
	public isHelpByNavHelp: boolean = false;
	public isVisibleHelpNoPermissionsCamera: boolean = false;
	public isVisibleChangeDevice: boolean = false;
	public permissionsCamera: boolean = true;
	private cameraPermissionsReady = false;
	private cameraPermissionsTimerService = inject(CameraPermissionsTimerService);
	public navsHelp: Array<INavHelp> = this.navHelpService.getNavsHelp();
	public navsHelpTutorial: Array<INavHelp> = this.navHelpService.getTutorialNavsHelp();
	private destroy$: Subject<void> = new Subject<void>();
	private languagePickerUi = inject(LanguagePickerUiService);
	public pageWarning: IPageWarning = { show: false, type: null };
	public backToOcrPendingErrorCode: number | null = null;
	public PAGE_WARNING = PAGE_WARNING;
	public handsetLandscape: boolean = false;
	public tutorialLottieOptions: AnimationOptions | null = null;
	private tutorialLottieSub: Subscription | null = null;
	/** Ring buffer de frames JPEG mientras el preview está abierto (NIF-822). */
	private silentFrameBuffer: string[] = [];
	/** Snapshot del buffer al pulsar foto (se envía con la selfie oficial). */
	private capturedSilentFrames: string[] = [];
	private silentFrameIntervalId: ReturnType<typeof setInterval> | null = null;
	/** NIF-1033: motivo fino de cancelación para Cloud Logs. */
	private lastCancelReason: string | null = null;
	private lastCancelHttp: { error_code?: number | string; details_code?: number | string; retry?: boolean } | null =
		null;
	private screenStartedAt: number = Date.now();
	private readonly userAgentService = inject(UserAgentService);
	/**
	 * Preview con el mismo patrón que POL activo: `fillParent` + zoom (móvil 1.55).
	 * Sin `fillParent`, el scale rompe el clip-path de `tec-scan-face`.
	 */
	public readonly polStaticPreviewZoomFactor: number;
	/** Zoom-out solo en JPEG de captura (NIF-951). */
	private readonly polStaticPayloadZoomFactor: number;
	private readonly polStaticPayloadUseFullCanvas: boolean;

	constructor(
		public lifeCheckService: LifeCheckService,
		public progressService: ProgressService,
		public tts: TextToSpeechService,
		public audioService: AudioService,
		public cloudLogsService: CloudLogsService,
		public designService: DesignService,
		public auth: AuthService,
		private processService: ProcessService,
		private breakpointObserver: BreakpointObserver,
		private fullscreenService: FullscreenService,
		private cookieService: CookieService,
		private trans: TranslateService,
		private loadingService: LoadingService,
		private cameraService: CameraService,
		private screenShotService: ScreenshotService,
		private lottieThemeService: LottieThemeService,
		private navHelpService: NavHelpService,
		private streamingManagementService: StreamingManagementService,
		private errorService: ErrorService,
		public timerManagerService: PolTimerManagerService,
		private masterService: MasterService,
		private identitySDKService: IdentitySDKService,
		private koDebugImagesService: KoDebugImagesService,
	) {
		const polDevice: PolPreviewDevice = this.userAgentService.isMobile() ? 'mobile' : 'pc';
		this.polStaticPreviewZoomFactor = getPolAnalysisExtraZoomFactor(polDevice);
		this.polStaticPayloadZoomFactor = getPolStaticPayloadZoomFactor(polDevice);
		this.polStaticPayloadUseFullCanvas = getPolStaticPayloadUseFullCanvas(polDevice);

		this.breakpointObserver
			.observe([Breakpoints.HandsetLandscape])
			.pipe(takeUntil(this.destroy$))
			.subscribe((result) => {
				this.handsetLandscape = result.matches;
				if (this.handsetLandscape) {
					this.stopTotalTime();
					this.stopManualTime();
					if (!this.pageWarning.show) {
						this.startWarningTime();
					}
				} else {
					this.startTotalTime();
					if (!this.pageWarning.show) {
						this.stopWarningTime();
					}
				}
				this.updateSubtitle();
			});

		this.activateLoading('GENERAL.LOADING.LIFE_CHECK_INITIAL');
	}

	ngOnInit(): void {
		this.refreshTutorialLottieOptions();
		this.designService
			.getConfigDesign()
			.pipe(takeUntil(this.destroy$))
			.subscribe(() => this.refreshTutorialLottieOptions());

		this.cameraService.allCameras$.pipe(takeUntil(this.destroy$)).subscribe((cameras: MediaDeviceInfo[]) => {
			this.allCameras = cameras;
			if (this.allCameras.length && this.manualLifeCheckConf?.general?.observations) {
				this.manualLifeCheckConf.general.observations.push(JSON.stringify(this.allCameras));
			}
			if (this.controls) {
				this.controls = {
					...this.controls,
					mediaDevices: this.allCameras,
				};
			} else {
				this.controls = {
					mediaDevices: this.allCameras,
				};
			}
			const foundIndex = this.navsHelp.findIndex((nav: INavHelp) => nav.id === NavHelpAction.ROTATE);
			if (this.allCameras && this.allCameras.length > 1) {
				if (foundIndex === -1) {
					this.navsHelp = this.navHelpService.getNavsHelpWithCamera();
				}
			} else {
				if (foundIndex > -1) {
					this.navsHelp.splice(foundIndex, 1);
				}
			}
		});
	}

	ngAfterViewInit(): void {
		this.screenStartedAt = Date.now();
		// Asegurar que las cámaras se carguen inmediatamente para que estén disponibles cuando se envíe el resultado
		this.cameraService.getAllCameras();

		// Iniciar operación
		this.masterService.getMaster().subscribe((master: IMaster) => {
			if (master && !this.initOperation) {
				this.initOperation = true;
				this.processService.initOperation(this.operation.type);
			}
		});
		this.checkCameraPermissions();

		// NIF-1033: 1× montaje PoL Manual / tec-scan-face
		this.cloudLogsService.sendCloudLogs(
			{ event: 'polManualMount', operationType: 'POL', scanFace: true },
			PolManualLog.PRELOAD,
			LogType.Info,
		);

		// Sin tutorial
		if (!this.operation.config.tutorial) {
			this.audioService.preloadLifeCheckAudios();
		}
		if (window.location.hostname !== 'localhost' && environment.saveScreenShot) {
			this.screenShotService.startSaveScreenshot(PolManualLog.SCREENSHOT);
		}
	}

	/**
	 * Marca la operación actual como completada y actualiza el estado de la operación de tipo FINISH.
	 *
	 * @param status El estado de la operación que se debe establecer en la operación de tipo FINISH.
	 */
	public nextOperation(status: OPERATION_STATUS, process: IOperation[]): void {
		const ocrOperation = process?.find((s: IOperation) => s.type === OPERATION_TYPE.OCR);
		if (process && ocrOperation) {
			if (!ocrOperation.completed) {
				// Reintento de OCR pendiente
				this.backToOcrPendingErrorCode = ocrOperation.pending_error_code ?? null;
				this.setPageWarning(PAGE_WARNING.BACK_TO_OCR);
				if (String(this.backToOcrPendingErrorCode) === ERROR_CODE.NO_FACE_IN_DOCUMENT) {
					this.tts.speak('OCR.FRONT.INSTRUCTIONS.TEXT.ONWARNING', true);
					this.tts.speak('OCR.AUDIO.IS_BLURRY_1', true);
				}
				this.stopTotalTime();
				setTimeout(() => {
					this.setPageWarning(null, false);
					this.operation.completed = true;
					this.operationService.setOperations(process);
				}, POL_BACK_TO_OCR_TIME * 1000);
				return;
			} else if (ocrOperation.state === OPERATION_STATUS.KO) {
				// OCR fallido
				this.verificationFail(TEST_STATUS.KO);
				return;
			}
		}

		this.operation.completed = true;
		const found = this.operations.find((s: IOperation) => s.type === OPERATION_TYPE.FINISH);
		if (found) {
			(found as IFinishOperation).config.status = status;
		}
	}

	get backToOcrTitleKey(): string {
		return String(this.backToOcrPendingErrorCode) === ERROR_CODE.NO_FACE_IN_DOCUMENT
			? 'OCR.FRONT.INSTRUCTIONS.TEXT.ONWARNING'
			: 'LIFE_CHECK.PAGE_WARNING.BACK_TO_OCR.TITLE';
	}

	get backToOcrShowSubtitle(): boolean {
		return String(this.backToOcrPendingErrorCode) !== ERROR_CODE.NO_FACE_IN_DOCUMENT;
	}

	private startWebcam(): void {
		if (this.permissionsCamera && !this.cameraService.hasGetUserMedia()) {
			this.setPageWarning(PAGE_WARNING.CAMERA_ERROR);
			this.cloudLogsService.sendCloudLogs(
				{ getUserMedia: 'getUserMedia is not supported by your browser' },
				PolManualLog.PRELOAD,
				LogType.Error,
			);
			this.operation.config.iframe ? this.loadingService.hideIframe() : this.loadingService.hide();
		}
	}

	/*
	 * Service enviar datos
	 */
	public sendResult(valid: boolean, status: TEST_STATUS = TEST_STATUS.OK): void {
		// Actualizamos el tiempo de finalización
		this.manualLifeCheckConf.general.finishAt = new Date().toISOString();
		// Detenemos el timer
		this.subscriptionTimers.unsubscribe();
		// Activamos el loading
		this.activateLoading();
		// Añadimos los datos de la prueba de vida al resultado
		// this.addDataToReturn();

		if (!valid) {
			// NIF-1033: motivo en memoria; log canónico en verificationFail. maxTime usa cancelReason dedicado.
			this.lastCancelReason = this.lastCancelReason === 'maxTime' ? 'maxTime' : status;
			this.lastCancelHttp = null;
		}

		// Lanzamos petición al servidor.
		// 	Si responde OK, se continúa con el siguiente paso.
		// 	Si responde KO, mandar al FINISH.
		this.lifeCheckService.sendLifeCheckResult(this.getBodyResult(valid, status)).subscribe({
			next: (response: IPolResponse | any) => {
				// TODO: gestionar de forma correcta
				if (response.result === OPERATION_STATUS.OK) {
					// Notificar al SDK el éxito del POL manual
					this.identitySDKService.notifyOperationSuccess(OperationType.POL_MANUAL, {
						result: response.result,
						response: response,
						status: status,
					});

					this.nextOperation(OPERATION_STATUS.OK, response.process);
				} else {
					const error = { error: response as unknown as IPolErrorResponse };
					this.handleSendResultError(error, status);
				}
			},
			error: (error: { error: IPolErrorResponse }) => {
				this.handleSendResultError(error, status);
			},
			complete: () => {
				this.loading.show = false;
				this.screenShotService.stopSaveScreenshot();
			},
		});
	}

	/**
	 * Maneja los errores que se producen al enviar el resultado de la verificación de vida.
	 *
	 * @param error El error recibido de la respuesta del servicio de verificación de vida
	 * @param status El estado de la verificación de vida en el momento del error
	 * @returns void
	 */
	private handleSendResultError(error: { error: IPolErrorResponse }, status: TEST_STATUS): void {
		// Notificar error al SDK
		this.identitySDKService.notifyOperationError(
			OperationType.POL_MANUAL,
			error?.error?.error?.msg || 'POL Manual Error',
			{
				errorCode: error?.error?.error?.error_code,
				status: status,
			},
		);

		this.lastCancelReason = this.lastCancelReason ?? status;
		this.lastCancelHttp = {
			error_code: error?.error?.error?.error_code,
			details_code: (error?.error as { details_code?: number | string })?.details_code,
			retry: error?.error?.retry,
		};

		// Error general
		if (this.errorService.hasGeneralError({ ...error?.error })) {
			this.screenShotService.stopSaveScreenshot();
			return;
		}

		// Error controlado
		const errorCode: ERROR_CODE = error?.error?.error?.error_code;
		if (errorCode) {
			console.log('Error controlado');
			// Tratar el código de error interno
			this.handleInternalCodeError(errorCode);

			const retry = error?.error?.retry ?? false;

			if (error?.error?.result === OPERATION_STATUS.KO && !retry) {
				this.verificationFail(TEST_STATUS.KO);
				return;
			}
			this.verificationFail(status);
		} else {
			// Error no controlado
			// TODO: estandarizar
			this.handleUncaughtError(status);
		}
		this.screenShotService.stopSaveScreenshot();
	}

	/**
	 * Maneja los errores internos relacionados con códigos específicos de error.
	 *
	 * @param errorCode El código de error de tipo `ERROR_CODE` que se ha producido.
	 *
	 * @see {@link https://docs.google.com/document/d/1Ei8S6iVtO9VDZus5y-vde3ZIIOoZIWLL1ctdKkKhp78/edit?pli=1&tab=t.0#heading=h.4w8hp5awjwp2} Documento de referencia de códigos de error.
	 */
	private handleInternalCodeError(errorCode: ERROR_CODE): void {
		// https://docs.google.com/document/d/1Ei8S6iVtO9VDZus5y-vde3ZIIOoZIWLL1ctdKkKhp78/edit?pli=1&tab=t.0#heading=h.4w8hp5awjwp2
		// TODO
		console.error(`Error code: ${errorCode}`);
	}

	/**
	 * Maneja errores no capturados durante el proceso de verificación de vida.
	 *
	 * Esta función muestra una advertencia de error en el servidor, detiene el indicador de carga,
	 * envía los logs correspondientes a la nube y, tras un tiempo determinado, notifica al iframe
	 * que el resultado ha sido cancelado por error.
	 *
	 * @param status El estado de la verificación de vida en el momento del error.
	 */
	private handleUncaughtError(status: TEST_STATUS): void {
		this.setPageWarning(PAGE_WARNING.SERVER_ERROR);
		this.loading.show = false;
		this.cloudLogsService.sendCloudLogs(
			{ status: status, observations: PAGE_WARNING.SERVER_ERROR },
			PolManualLog.FINISH,
			LogType.Error,
		);
	}

	/**
	 * Maneja el fallo en la verificación de vida.
	 *
	 * Esta función reproduce un mensaje de texto a través de TTS, envía los logs correspondientes a la nube,
	 * muestra una advertencia en la página y, tras un tiempo determinado, envía el resultado KO al iframe.
	 *
	 * @param status El estado de la verificación de vida que ha fallado.
	 */
	private verificationFail(status: TEST_STATUS): void {
		this.operation.completed = true;
		if (!this.lastCancelReason) {
			this.lastCancelReason = status;
		}
		const videoWidth = this.video?.videoWidth ?? 0;
		const videoHeight = this.video?.videoHeight ?? 0;
		this.cloudLogsService.sendCloudLogs(
			{
				status,
				observations: PAGE_WARNING.VERIFICATION_FAIL,
				cancelReason: this.lastCancelReason,
				testStatus: status,
				operationType: 'POL',
				tutorialActive: Boolean(this.operation?.config?.tutorial),
				pageWarning: { type: this.pageWarning?.type, show: this.pageWarning?.show },
				warningTime: this.timerManagerService.warningTime,
				manualTime: this.timerManagerService.manualTime,
				maxTime: this.manualLifeCheckConf?.general?.maxTime,
				screenDurationMs: Date.now() - this.screenStartedAt,
				hasVideoFrame: videoWidth > 0,
				videoWidth,
				videoHeight,
				faceCaptured: Boolean(this.imageData16x9),
				silentFramesBuffered: this.silentFrameBuffer?.length ?? 0,
				error_code: this.lastCancelHttp?.error_code,
				details_code: this.lastCancelHttp?.details_code,
				retry: this.lastCancelHttp?.retry,
			},
			PolManualLog.FINISH,
			LogType.Warning,
		);
		this.processService.unSubcribeCheckStatus();
		this.operations.forEach((op: IOperation) => {
			if (op.type !== OPERATION_TYPE.FINISH) {
				op.completed = true;
			}
		});
		const foundFinish = this.operations.find((op: IOperation) => op.type === OPERATION_TYPE.FINISH);
		if (foundFinish) {
			(foundFinish as IFinishOperation).config.status =
				status === TEST_STATUS.OK ? OPERATION_STATUS.OK : OPERATION_STATUS.KO;
		} else {
			this.setPageWarning(PAGE_WARNING.VERIFICATION_FAIL);
		}
	}

	/**
	 * Genera el cuerpo del resultado para el proceso de verificación de vida.
	 *
	 * @param valid Indica si la verificación de vida fue válida.
	 * @param status Estado actual de la verificación de vida.
	 * @returns Un objeto `IPolPayload` que contiene las imágenes capturadas, las cámaras utilizadas,
	 *          los datos serializados del resultado y el estado de validez.
	 */
	private getBodyResult(valid: boolean, status: TEST_STATUS): IPolPayload {
		const photoImg = this.manualLifeCheckConf.general.photo?.img;
		const { images, images_pol_proof } = buildManualPolImagesPayload(photoImg, this.capturedSilentFrames);

		this.koDebugImagesService.sendManualPolKoDebugLogs(valid, status, photoImg, {
			operationType: this.operation?.type,
		});

		// Usar allCamerasValue para obtener el valor actual sincronizadamente
		// Esto asegura que siempre tengamos las cámaras disponibles cuando se envía el resultado
		let camerasToUse: MediaDeviceInfo[] = this.allCameras;
		if (this.cameraService.allCamerasValue && this.cameraService.allCamerasValue.length > 0) {
			camerasToUse = this.cameraService.allCamerasValue;
		}
		const cameras: Array<string> = camerasToUse.map((c: MediaDeviceInfo) => c.label);

		const generalData = { ...this.manualLifeCheckConf.general };
		if (generalData.photo?.img) {
			delete generalData.photo.img;
		}

		return {
			images,
			images_pol_proof,
			cameras,
			device_id: this.auth.getDeviceId(),
			data: JSON.stringify([{ general: generalData }, { status }]),
			status: valid,
		};
	}

	/*
	 * Controles de la cámara
	 */
	public onCameraError(err: any): void {
		this.operation.config.iframe ? this.loadingService.hideIframe() : this.loadingService.hide();
		this.hidePreviewLoading();
		this.manualLifeCheckConf.general.observations.push(
			`Error: onCameraError - ${err?.toString()}`,
			PolManualLog.PRELOAD,
		);
		if (err?.name === 'NotAllowedError') {
			this.permissionsCamera = false;
			this.updateSubtitle();
			this.onPermissionsDenied();
		} else {
			this.setPageWarning(PAGE_WARNING.CAMERA_ERROR);
			this.stopTotalTime();
			this.stopManualTime();
			this.startWarningTime();
		}
	}

	public onCameraPermissionRevoked(): void {
		this.operation.config.iframe ? this.loadingService.hideIframe() : this.loadingService.hide();
		this.hidePreviewLoading();
		this.permissionsCamera = false;
		this.updateSubtitle();
		this.onPermissionsDenied();
	}

	public onCameraStarted(camera: {
		stream: MediaStream;
		video: HTMLVideoElement;
		canvas: HTMLCanvasElement;
		ctx: CanvasRenderingContext2D;
	}): void {
		this.video = camera.video;
		this.canvas = camera.canvas;
		this.ctx = camera.ctx;
		this.stream = camera.stream;

		if (!this.cameraPermissionsReady) {
			this.permissionsCamera = true;
			this.onPermissionsGranted();
		} else {
			this.resetTestClocksAfterPermissionsGranted();
			this.resumeTestTimersIfApplicable();
		}

		this.getAllCameras();

		// Esperar a que el streaming esté completamente activo antes de continuar
		void this.waitForStreamingAndStartProcessing().catch(() => undefined);
	}

	/**
	 * Espera a que el streaming esté activo antes de iniciar el procesamiento manual de prueba de vida
	 * Si required_recording está activo y el streaming falla, ABORTA la operación (modo estricto)
	 */
	private async waitForStreamingAndStartProcessing(): Promise<void> {
		// Verificar si el streaming está habilitado en la configuración
		const isRecordingRequired = await this.streamingManagementService.isRecordingRequired();

		if (isRecordingRequired) {
			if (this.streamingManagementService.isStreamingRetryInProgress()) {
				await this.streamingManagementService.waitUntilStreamingRetryIdle();
			} else {
				try {
					// Esperar a que el streaming esté listo antes de continuar
					await this.streamingManagementService.waitForStreamingReady();
				} catch (streamingError: any) {
					// Si required_recording está activo, el streaming es OBLIGATORIO
					this.cloudLogsService.sendCloudLogs(
						{ pol: `Streaming connection failed - Aborting: ${streamingError?.toString()}` },
						PolManualLog.IN_PROCESS,
						LogType.Error,
					);

					if (!this.streamingManagementService.isStreamingRetryInProgress()) {
						// Mostrar pantalla de error con botón de reintento
						this.errorService.setStreamingError(async () => {
							await this.streamingManagementService.retryStreaming(() => this.errorService.clearError());
						});
					}
					throw streamingError;
				}
			}
		}

		try {
			await waitForVideoPreviewReady(this.video);

			// Ocultar el loading y permitir que el procesamiento comience
			this.operation.config.iframe ? this.loadingService.hideIframe() : this.loadingService.hide();
			this.hidePreviewLoading();

			this.startSilentFrameCapture();

			// Actualizamos el subtitle
			this.updateSubtitle();
		} catch (error: any) {
			// Error crítico no relacionado con streaming
			this.cloudLogsService.sendCloudLogs(
				{ pol: `Critical error in waitForStreamingAndStartProcessing: ${error?.toString()}` },
				PolManualLog.IN_PROCESS,
				LogType.Error,
			);
			this.operation.config.iframe ? this.loadingService.hideIframe() : this.loadingService.hide();
			this.hidePreviewLoading();

			this.startSilentFrameCapture();

			// Actualizamos el subtitle
			this.updateSubtitle();
		}
	}

	private getAllCameras(): void {
		this.cameraService.getAllCameras();
	}

	/*
	 * Controles de cada prueba de vida
	 */

	public changeDevice(): void {
		if (this.operation.config.iframe) {
			this.isVisibleChangeDevice = true;
		}
		if (!this.operation.config.iframe) {
			this.isVisibleChangeDevice = true;
		}
	}

	public checkPermissions(): void {
		this.checkCameraPermissions();
	}

	private checkCameraPermissions(): void {
		const seq = this.cameraService.beginPermissionCheck();
		// Arrancamos el reloj justo al iniciar la petición de permisos: así el KO por timeout
		// también cubre el caso en que el usuario no responde al prompt del navegador.
		this.cameraPermissionsTimerService.startWait(() => this.onCameraPermissionsTimeout());
		this.operation.config.iframe
			? this.loadingService.showIframe(this.trans.instant('GENERAL.LOADING.PERMISSIONS'))
			: this.loadingService.show(this.trans.instant('GENERAL.LOADING.PERMISSIONS'));
		this.cameraService
			.resolveHasCameraPermission(this.lifeCheckService.constraints)
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (hasPermission: boolean) => this.finishPermissionCheck(seq, hasPermission),
				error: () => this.finishPermissionCheck(seq, false),
			});
	}

	private finishPermissionCheck(seq: number, hasPermission: boolean): void {
		if (!this.cameraService.isPermissionCheckCurrent(seq)) {
			return;
		}
		if (hasPermission) {
			this.permissionsCamera = true;
			this.onPermissionsGranted();
			this.activatePreviewLoading('GENERAL.LOADING.CAMERA_STARTING');
			setTimeout(() => {
				this.startWebcam();
			}, 500);
		} else {
			this.permissionsCamera = false;
			this.updateSubtitle();
			this.onPermissionsDenied();
			this.hidePreviewLoading();
		}
		this.operation.config.iframe ? this.loadingService.hideIframe() : this.loadingService.hide();
	}

	private canRunTestTimers(): boolean {
		return this.permissionsCamera && this.cameraPermissionsReady;
	}

	private onPermissionsDenied(): void {
		this.stopActiveGuidanceAudio();
		this.cameraPermissionsReady = false;
		this.cameraService.releaseActiveStream();
		this.stopManualTime();
		this.stopTotalTime();
		this.stopWarningTime();
		this.cameraPermissionsTimerService.startWait(() => this.onCameraPermissionsTimeout());
	}

	private stopActiveGuidanceAudio(): void {
		this.tts.stop();
		this.audioService.stop();
	}

	private onPermissionsGranted(): void {
		this.cameraPermissionsTimerService.stopWait();
		this.cameraPermissionsReady = true;
		this.resetTestClocksAfterPermissionsGranted();
		this.resumeTestTimersIfApplicable();
	}

	private resetTestClocksAfterPermissionsGranted(): void {
		this.stopManualTime();
		this.stopTotalTime();
		this.timerManagerService.resetTotalTime();
	}

	private resumeTestTimersIfApplicable(): void {
		if (this.operation?.config?.tutorial && !this.pageWarning.show && !this.handsetLandscape) {
			this.startWarningTime();
		}
		if (!this.handsetLandscape && !this.pageWarning.show) {
			this.startTotalTime();
			this.startManualTime();
		}
	}

	private onCameraPermissionsTimeout(): void {
		// `cameraPermissionsReady` solo pasa a true en onPermissionsGranted(): si seguimos aquí,
		// el usuario no ha concedido permisos (haya respondido NO o no haya respondido al prompt).
		if (this.cameraPermissionsReady) {
			return;
		}
		this.cloudLogsService.sendCloudLogs(
			{
				observations: 'Camera and microphone permissions timeout',
				status: TEST_STATUS.KO_PERMISSION,
			},
			PolManualLog.FINISH,
			LogType.Warning,
		);
		this.sendResult(false, TEST_STATUS.KO_PERMISSION);
	}

	/*
	 * Gestión de los textos
	 */
	public updateSubtitle(): void {
		this.subtitle = this.generateSubtitle();
	}

	/*
	 * Nav Help
	 */
	public navHelpChange(action: INavHelp): void {
		const actions = {
			[NavHelpAction.ROTATE]: () => this.changeCamera(),
			[NavHelpAction.QR]: () => (this.isVisibleScan = true),
			[NavHelpAction.HELP]: () => this.displayHelpWarning(true),
			[NavHelpAction.LANGUAGE]: () => this.languagePickerUi.open(),
			[NavHelpAction.VOLUMEN]: () => this.navHelpService.toggleAudioGuide(action),
		};
		return actions[action.id]();
	}

	public changeCamera(): void {
		this.cameraService.changeCamera();
	}

	public onCameraSwitchStarted(): void {
		onCameraPreviewSwitchStarted({
			activatePreviewLoading: (key) => this.activatePreviewLoading(key),
			hidePreviewLoading: () => this.hidePreviewLoading(),
		});
	}

	public onCameraSwitchCompleted(): void {
		onCameraPreviewSwitchCompleted({
			activatePreviewLoading: (key) => this.activatePreviewLoading(key),
			hidePreviewLoading: () => this.hidePreviewLoading(),
		});
	}

	/*
	 * Timer
	 */
	// Total time
	private startManualTime(): void {
		if (!this.canRunTestTimers()) return;
		this.timerManagerService.startManualTime(() => {
			if (this.timerManagerService.manualTime >= this.manualLifeCheckConf.general.maxTime) {
				this.stopManualTime();
				this.manualLifeCheckConf.general.status = TestStatus.OnFail;
				// NIF-1033: cancelReason maxTime en Cloud Logs; API sigue con KO.
				this.lastCancelReason = 'maxTime';
				this.sendResult(false, TEST_STATUS.KO);
			}
		});
	}

	public stopManualTime(): void {
		this.timerManagerService.stopManualTime();
	}

	private startTotalTime(): void {
		if (!this.canRunTestTimers()) return;
		this.timerManagerService.startTotalTime();
	}

	private stopTotalTime(): void {
		this.timerManagerService.stopTotalTime();
	}

	private stopMiddleFaceAwayTime(): void {
		this.timerManagerService.stopMiddleFaceAwayTime();
	}

	// Warning time
	private startWarningTime(): void {
		if (!this.canRunTestTimers()) return;
		this.timerManagerService.startWarningTime(() => {
			if (this.timerManagerService.warningTime >= POL_WARNING_TIME) {
				this.stopWarningTime();
				this.manualLifeCheckConf.general.status = TestStatus.OnFail;
				this.sendResult(false, this.getLivenessStatus());
			}
		});
	}

	public stopWarningTime(): void {
		this.timerManagerService.stopWarningTime();
	}

	// KO Help time
	private startKOHelpTime(): void {
		this.timerManagerService.startKOHelpTime(() => {
			if (this.timerManagerService.KOHelpTime >= POL_WARNING_TIME) {
				this.stopKOHelpTime();
				this.lastCancelReason = TEST_STATUS.KO_HELP;
				this.verificationFail(TEST_STATUS.KO_HELP);
			}
		});
	}

	public stopKOHelpTime(): void {
		this.timerManagerService.stopKOHelpTime();
	}

	private getLivenessStatus(): TEST_STATUS {
		if (this.pageWarning.show) {
			switch (this.pageWarning.type) {
				case PAGE_WARNING.HELP:
					return TEST_STATUS.KO_HELP;
				case PAGE_WARNING.NO_FACE:
					return TEST_STATUS.KO_NO_FACE;
				case PAGE_WARNING.MULTIPLE_FACES:
					return TEST_STATUS.KO_MULTIPLE_FACES;
				case PAGE_WARNING.CAMERA_ERROR:
					return TEST_STATUS.KO_CAMERA;
			}
		}
		if (this.handsetLandscape) {
			return TEST_STATUS.KO_HANDSET_LANDSCAPE;
		}
		if (this.operation.config.tutorial) {
			return TEST_STATUS.KO_TUTORIAL;
		}
		return TEST_STATUS.KO;
	}

	private getTutorialLottiePath(): string {
		return this.designService.isDarkMode()
			? 'assets/animations/pwcs-prueba-vida-dark.json'
			: 'assets/animations/pwcs-prueba-vida-light.json';
	}

	private refreshTutorialLottieOptions(): void {
		const path = this.getTutorialLottiePath();
		this.tutorialLottieSub?.unsubscribe();
		this.tutorialLottieSub = this.lottieThemeService
			.loadThemedAnimation(path)
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (options) => {
					this.tutorialLottieOptions = options ?? { path };
				},
				error: () => {
					this.tutorialLottieOptions = { path };
				},
			});
	}

	/*
	 * Tutorial
	 */
	public async finishTutorial(): Promise<void> {
		this.operation.config.tutorial = false;
		this.stopWarningTime();
		this.audioService.preloadLifeCheckAudios();

		// Verificar streaming antes de permitir continuar (modo estricto)
		try {
			const isRecordingRequired = await this.streamingManagementService.isRecordingRequired();

			if (isRecordingRequired) {
				try {
					// Esperar a que el streaming esté listo antes de continuar
					await this.streamingManagementService.waitForStreamingReady();
				} catch (streamingError: any) {
					// MODO ESTRICTO: Si required_recording está activo, el streaming es OBLIGATORIO
					this.cloudLogsService.sendCloudLogs(
						{ pol: `Streaming connection failed after tutorial - Aborting: ${streamingError?.toString()}` },
						PolManualLog.IN_PROCESS,
						LogType.Error,
					);

					if (!this.streamingManagementService.isStreamingRetryInProgress()) {
						// Mostrar pantalla de error con botón de reintento
						this.errorService.setStreamingError(async () => {
							await this.streamingManagementService.retryStreaming(() => this.errorService.clearError());
						});
					}
					return;
				}
			}
		} catch (error: any) {
			this.cloudLogsService.sendCloudLogs(
				{ pol: `Error checking streaming in finishTutorial: ${error?.toString()}` },
				PolManualLog.IN_PROCESS,
				LogType.Error,
			);
		}

		if (!this.operation.config.iframe) {
			this.fullscreenService.enterFullscreen();
		}
		this.updateSubtitle();
	}

	private resetTimers(): void {
		if (this.subscriptionTimers) {
			this.subscriptionTimers.unsubscribe();
			this.subscriptionTimers = new Subscription();
		}
	}

	private setPolSetting(config: IPolOperation['config']): IPolManualConf {
		return {
			...manualLifeCheckConf,
			general: {
				...manualLifeCheckConf.general,
				maxTime: config?.max_time ?? manualLifeCheckConf.general.maxTime,
			},
		};
	}

	private displayHelpWarning(isHelpByNavHelp: boolean): void {
		this.isHelpByNavHelp = isHelpByNavHelp;
		this.setPageWarning(PAGE_WARNING.HELP);
		this.stopTotalTime();
		this.stopManualTime();
		// Si es help por segundo intento (!isHelpByNavHelp),
		// iniciar timer para "No hemos podido verificar tu identidad"
		if (!isHelpByNavHelp) this.startKOHelpTime();
	}

	/**
	 * Establece el tipo y la visibilidad de la advertencia en la página.
	 *
	 * Esta función actualiza el objeto `pageWarning` para mostrar u ocultar una advertencia en la interfaz,
	 * y define el tipo de advertencia que se debe mostrar.
	 *
	 * @param type El tipo de advertencia de la página, puede ser `PAGE_WARNING` o `null`.
	 * @param show Indica si se debe mostrar la advertencia en la página. Por defecto es `true`.
	 */
	public setPageWarning(type: PAGE_WARNING | null, show: boolean = true): void {
		this.pageWarning.show = show;
		this.pageWarning.type = type;

		// Actualizamos el subtitle
		this.updateSubtitle();
	}

	/**
	 * Activa el indicador de carga mostrando un mensaje.
	 *
	 * @param textKey Clave opcional para el texto de carga a mostrar. Si no se proporciona, se utiliza 'GENERAL.LOADING.LIFE_CHECK_VALIDATION' por defecto.
	 */
	private activateLoading(textKey?: string): void {
		this.loading = {
			show: true,
			text: this.trans.instant(textKey ?? 'GENERAL.LOADING.LIFE_CHECK_VALIDATION'),
		};
	}

	/**
	 * Reproduce un mensaje de texto usando el TextToSpeechService solo si el texto actual ha cambiado.
	 *
	 * @param currentText - El texto actual mostrado.
	 * @param newText - El nuevo texto que se va a mostrar.
	 * @param textToSpeak - El texto que se debe reproducir por voz.
	 */
	private speakIfTextChanged(currentText: string, newText: string, textToSpeak: string): void {
		if (currentText === newText || this.sounded.includes(textToSpeak)) return;
		this.sounded.push(textToSpeak);
		this.tts.speak(textToSpeak);
	}

	// #region Gestión de los textos
	/**
	 * Genera el subtítulo adecuado para la interfaz de verificación de vida según el estado actual de la aplicación.
	 *
	 * Evalúa múltiples condiciones relacionadas con permisos de cámara, orientación del dispositivo, advertencias de página,
	 * estado de carga, y progreso de las pruebas de vida. Devuelve el texto traducido correspondiente para mostrar como subtítulo,
	 * o una cadena vacía si no corresponde mostrar ningún mensaje.
	 *
	 * Además, si el mensaje cambia, reproduce el audio asociado al nuevo texto.
	 *
	 * @returns {string} El subtítulo traducido para mostrar en la interfaz, o una cadena vacía si no aplica.
	 */
	private generateSubtitle(): string {
		if (
			!this.manualLifeCheckConf ||
			!this.permissionsCamera ||
			this.handsetLandscape ||
			this.pageWarning.type === PAGE_WARNING.VERIFICATION_FAIL ||
			this.pageWarning.type === PAGE_WARNING.SERVER_ERROR ||
			this.pageWarning.type === PAGE_WARNING.CAMERA_ERROR
		) {
			return '';
		}

		if (this.pageWarning.type === PAGE_WARNING.HELP) {
			return this.trans.instant('LIFE_CHECK.HELP.DESCRIPTION');
		}

		if (this.operation?.config.tutorial) {
			return this.trans.instant('LIFE_CHECK.MANUAL.TUTORIAL_TITLE');
		}

		if (this.loading.show || this.pageWarning.show || this.handsetLandscape) {
			return '';
		}
		const message = this.manualLifeCheckConf?.general?.photo
			? 'LIFE_CHECK.MANUAL.VALIDATION_PHOTO'
			: 'LIFE_CHECK.MANUAL.TAKE_PHOTO';
		const translatedMessage = this.trans.instant(message);
		this.speakIfTextChanged(this.subtitle, translatedMessage, message);

		return translatedMessage;
	}
	// #endregion

	/**
	 * Captura un JPEG del vídeo alineado con el preview (cover) + zoom-out de payload (NIF-951).
	 * El preview no cambia; selfie oficial y ring buffer (NIF-822) usan el mismo encuadre.
	 * @returns dataURL JPEG o null si no hay contexto/vídeo listo
	 */
	private captureCroppedJpeg(): string | null {
		if (!this.ctx || !this.video || !this.canvas) {
			return null;
		}
		if (!this.video.videoWidth || !this.video.videoHeight) {
			return null;
		}

		const preview = this.video.getBoundingClientRect();
		const viewportWidth = preview.width > 0 ? preview.width : this.video.videoWidth;
		const viewportHeight = preview.height > 0 ? preview.height : this.video.videoHeight;

		drawVideoCoverFrameToCanvas(
			this.video,
			this.canvas,
			this.ctx,
			viewportWidth,
			viewportHeight,
			this.polStaticPayloadZoomFactor,
		);

		if (this.polStaticPayloadUseFullCanvas) {
			return this.canvas.toDataURL('image/jpeg', quality);
		}

		const { srcX, srcY, srcWidth, srcHeight } = getPolLivenessJpegCrop(this.canvas.width, this.canvas.height);
		if (srcWidth <= 0 || srcHeight <= 0) {
			return null;
		}

		const tempCanvas = document.createElement('canvas');
		tempCanvas.width = srcWidth;
		tempCanvas.height = srcHeight;
		const tempCtx = tempCanvas.getContext('2d');
		if (!tempCtx) {
			return null;
		}

		tempCtx.drawImage(this.canvas, srcX, srcY, srcWidth, srcHeight, 0, 0, tempCanvas.width, tempCanvas.height);
		return tempCanvas.toDataURL('image/jpeg', quality);
	}

	/**
	 * Crea una imagen recortada para la selfie oficial / preview.
	 */
	private drawImageForDocumentValidation(): void {
		const dataUrl = this.captureCroppedJpeg();
		if (dataUrl) {
			this.imageData16x9 = dataUrl;
		}
	}

	/** Arranca el ring buffer silencioso de frames (NIF-822). */
	private startSilentFrameCapture(): void {
		this.stopSilentFrameCapture();
		if (this.manualLifeCheckConf?.general?.photo) {
			return;
		}
		this.silentFrameBuffer = [];
		this.silentFrameIntervalId = setInterval(() => this.pushSilentFrame(), SILENT_FRAME_INTERVAL_MS);
	}

	private stopSilentFrameCapture(): void {
		if (this.silentFrameIntervalId !== null) {
			clearInterval(this.silentFrameIntervalId);
			this.silentFrameIntervalId = null;
		}
	}

	private pushSilentFrame(): void {
		if (this.manualLifeCheckConf?.general?.photo) {
			return;
		}
		if (!this.video?.videoWidth || !this.ctx || !this.canvas) {
			return;
		}

		this.canvas.width = this.video.videoWidth;
		this.canvas.height = this.video.videoHeight;
		const frame = this.captureCroppedJpeg();
		if (!frame) {
			return;
		}

		this.silentFrameBuffer.push(frame);
		if (this.silentFrameBuffer.length > SILENT_FRAME_BUFFER_SIZE) {
			this.silentFrameBuffer.shift();
		}
	}

	public takePicture(): void {
		this.capturedSilentFrames = [...this.silentFrameBuffer];
		this.stopSilentFrameCapture();

		this.canvas.width = JSON.parse(JSON.stringify(this.video.videoWidth));
		this.canvas.height = JSON.parse(JSON.stringify(this.video.videoHeight));
		this.ctx.drawImage(this.video, 0, 0, this.canvas.width, this.canvas.height);
		this.drawImageForDocumentValidation();
		this.manualLifeCheckConf.general.photo = {
			img: this.imageData16x9,
			label: this.cameraService.getCurrentDeviceLabel(),
		};
		(document.getElementById('uploadImage') as HTMLImageElement).src = this.manualLifeCheckConf.general.photo.img;
		this.updateSubtitle();
	}

	public repeatPhoto(): void {
		this.manualLifeCheckConf.general.photo = null;
		this.capturedSilentFrames = [];
		this.silentFrameBuffer = [];
		(document.getElementById('uploadImage') as HTMLImageElement).src = '';
		this.startSilentFrameCapture();
		this.updateSubtitle();
	}

	ngOnDestroy(): void {
		this.stopSilentFrameCapture();
		this.silentFrameBuffer = [];
		this.capturedSilentFrames = [];
		this.tutorialLottieSub?.unsubscribe();
		this.cameraPermissionsTimerService.stopWait();
		// Verificar si el streaming está activo para decidir si detener los tracks
		const isStreamingActive = this.streamingManagementService?.isStreamingActive() ?? false;

		// Desuscribirse de todos los timers
		this.video = null;
		// Detener TODOS los timers del servicio
		this.stopMiddleFaceAwayTime();
		this.stopTotalTime();
		this.stopManualTime();
		this.stopWarningTime();
		if (this.subscriptionTimers) {
			this.subscriptionTimers.unsubscribe();
		}

		// Solo detener los tracks si NO hay streaming activo
		if (!isStreamingActive && this.stream) {
			this.stream.getTracks().forEach((track) => track.stop());
		}

		this.stream = null;
		this.screenShotService.stopSaveScreenshot();
		this.destroy$.next();
		this.destroy$.complete();
	}

	private activatePreviewLoading(textKey: string): void {
		this.loading = {
			show: true,
			text: this.trans.instant(textKey),
		};
	}

	private hidePreviewLoading(): void {
		this.loading = { show: false, text: '' };
	}
}
